// Pre-sales on production runs (ERP-PLAN §5.14), spread into `mutations` (shared/mutations.ts): campaigns and their
// reservations per item, held units, income entries (with their payout statements as attachments) and shipments to
// backers (their units, pledge value and shipping costs). Rules and implementations: server/src/mutations/presales.ts.
// The import (node server/src/cli.ts presale-import) writes through the same rules (server/src/importers/presale.ts).
import { z } from "zod";
import { create, custom, remove, update } from "./mutation-kit.ts";
import { holdFields, incomeFields, presaleFields, presaleLineFields, presaleText, shipmentCostInput, shipmentFields } from "./presales.ts";

export const shipmentRecordArgs = shipmentFields.extend({
  id: z.uuid(),
  region: shipmentFields.shape.region.default("us"),
  packages: shipmentFields.shape.packages.default(0),
  pledge_value: shipmentFields.shape.pledge_value.default(0),
  note: shipmentFields.shape.note.default(""),
  lines: z
    .array(z.object({ item_id: z.uuid(), qty: z.number().int("Whole units").positive("Units shipped are more than 0").max(10_000_000) }))
    .min(1, "Say what was shipped")
    .max(500),
  costs: z.array(shipmentCostInput).max(20).default([]),
  /** Ship more of an item than is reserved anyway (a late add-on, a replacement). */
  confirm_over: z.boolean().optional(),
  external_ref: presaleText(200).optional(),
});

export const presaleMutations = {
  /** A campaign on a run (the browser makes the id): its channels, where its goods ship from, the counts' date. */
  "presale.create": create("presale", presaleFields.partial().required({ run_id: true, name: true })),
  "presale.update": update("presale", presaleFields.omit({ run_id: true })),
  /** Only with no income or shipments recorded on it (cancel it instead). */
  "presale.remove": remove("presale"),

  /** Units reserved for one item: US, international, no country yet; assigned colours and not shipping as info. */
  "presale_line.create": create("presale_line", presaleLineFields.partial().required({ presale_id: true, item_id: true })),
  "presale_line.update": update("presale_line", presaleLineFields.omit({ presale_id: true, item_id: true })),
  "presale_line.remove": remove("presale_line"),

  /** Units of a run set aside, not sold (gifts, influencers): received at HHL as usual, never planned as for sale. */
  "production_run_hold.create": create("production_run_hold", holdFields.partial().required({ run_id: true, item_id: true })),
  "production_run_hold.update": update("production_run_hold", holdFields.omit({ run_id: true, item_id: true })),
  "production_run_hold.remove": remove("production_run_hold"),

  /** Money in as paid out (payouts net of their fees), fees taken separately, refunds; a non-USD amount needs its rate. */
  "presale_income.create": create("presale_income", incomeFields.partial().required({ presale_id: true, received_on: true, amount: true })),
  "presale_income.update": update("presale_income", incomeFields.omit({ presale_id: true })),
  "presale_income.remove": remove("presale_income"),

  /**
   * What went to backers (a dated total, usually a month's per region): units per item, packages, the pledge value
   * they paid, and its shipping costs (each a run cost of category backer_shipping). Takes the units out of the CM:
   * local items leave its ledger (presale_ship moves), finished goods not reported built yet are reported first.
   * Refused (409) when an item would ship more than is reserved, unless confirm_over. The same id twice is a no-op.
   */
  "presale_shipment.record": custom(shipmentRecordArgs),
  "presale_shipment.update": update("presale_shipment", shipmentFields.omit({ presale_id: true })),
  /** Takes a shipment back: its ledger moves are reversed and its shipping costs removed (refused while one is on a bill). */
  "presale_shipment.remove": custom(z.object({ id: z.uuid() })),
  /**
   * Shipments recorded before any cost was known: their lines with no unit cost get today's (and a shipment its share
   * of the deposits, once the pre-sale's total pledge value is known). Lines that have one never change.
   */
  "presale_shipment.fill_costs": custom(z.object({ presale_id: z.uuid() })),
  /**
   * The CM won't build a make PO's remaining units for backers: they go back to planning (another open make PO takes
   * them, else they're still to build). Only once HHL's share is in or closed; the PO is then received.
   */
  "po.release_backers": custom(z.object({ id: z.uuid() })),
  /** Undoes "Nothing else is coming" on a PO still open for its backers' units: HHL expects the rest of its share again. */
  "po.reopen_hhl_share": custom(z.object({ id: z.uuid() })),
} as const;
